// Remember the last data of a page IN MEMORY, so coming back to it shows that data at once while a fresh copy is
// read quietly in the background. Pages that use it: Home and the Cases hub.
//
// WHAT KEEPS THIS SAFE
//   • Memory only. Nothing is written to localStorage, sessionStorage, IndexedDB or any other store: a reload, or
//     closing the tab, forgets everything, so no customer data rests in the browser.
//   • Keyed by USER and COMPANY and name. An entry for company A can never be read as company B's, and a different
//     signed-in user never sees the previous user's entries (switching user wipes the cache).
//   • Wiped completely on logout, when the signed-in user changes, on a company switch (clearAll, called by the
//     company switcher and by the page remount per company), and when the staff-access refusal screen appears
//     (nothing is remembered while that screen is up).
//   • Any action that WRITES (a non-GET through apiFetch, or a write to the data API through the Supabase client)
//     drops EVERYTHING remembered, at the start of the request and again when it ends, so the next visit to any
//     page reads fresh data instead of showing a state the action just changed.
//   • A read that was in flight when something above happened can never put its (possibly old) result back:
//     results are only stored if no wipe or write happened while they were being read; after a wipe (logout,
//     company switch) the result is not even delivered to the page.
import { STAFF_ACCESS_BLOCKED_EVENT } from './events';

let userId = null;
let blocked = false;
let epoch = 0;        // bumps on every complete wipe (identity change): results read before it are discarded
let writes = 0;       // bumps on every wipe AND every write: results read before it are not stored
const store = new Map();

const keyOf = (orgId, name) => `${userId}|${orgId}|${name}`;

/** Forget everything (logout, user change, company switch, staff refusal). */
export function clearAll() {
  store.clear();
  epoch += 1;
  writes += 1;
}

/** A write happened (or is happening): forget everything, and refuse to store reads that started before it. */
export function invalidateAll() {
  store.clear();
  writes += 1;
}

/** Tell the cache who is signed in. A different user (or none) wipes it. */
export function setCacheUser(id) {
  const next = id || null;
  if (next !== userId) {
    userId = next;
    clearAll();
  }
}

/** The remembered value, or undefined. Never returns anything for another user or company. */
export function peek(orgId, name) {
  if (blocked || !userId || !orgId) return undefined;
  const hit = store.get(keyOf(orgId, name));
  return hit ? hit.value : undefined;
}

/**
 * Stale-while-revalidate.
 *   1. If this user+company has a remembered value for `name`, hand it to onData(value, { cached: true }) at once.
 *   2. Read a fresh one with load() -> { value, cacheable = true }. Hand it to onData(value, { cached: false }) and
 *      remember it (unless it is not cacheable, or a write happened while it was being read).
 * After a complete wipe (logout, company switch) the fresh result is dropped and onData is not called.
 * Returns the fresh value (or undefined when it was discarded).
 */
export async function swr({ orgId, name, load, onData }) {
  const startEpoch = epoch;
  const startWrites = writes;
  const startUser = userId;
  const hit = peek(orgId, name);
  if (hit !== undefined) onData(hit, { cached: true });
  const out = await load();
  const value = out && Object.prototype.hasOwnProperty.call(out, 'value') ? out.value : out;
  const cacheable = !(out && out.cacheable === false);
  if (epoch !== startEpoch || userId !== startUser) return undefined;   // identity changed while reading: drop it
  if (cacheable && !blocked && writes === startWrites && userId && orgId) store.set(keyOf(orgId, name), { value });
  onData(value, { cached: false });
  return value;
}

/**
 * Does this request change data? (used by apiFetch and the Supabase client's fetch)
 * Only writes to the data and file APIs count; sign-in and token refresh calls do not.
 */
export function isDataWrite(url, method) {
  const m = String(method || 'GET').toUpperCase();
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return false;
  const u = String(url || '');
  if (u.includes('/auth/v1/')) return false;
  // A search is a POST (the term must not sit in a URL) but changes nothing.
  if (u.split('?')[0] === '/api/search') return false;
  // The agent Cases screens read with a POST (the supplier filter travels in the body, not in a URL) and change nothing.
  if (u.split('?')[0].startsWith('/api/agent-cases/')) return false;
  // The agent Reports read (and download) with a POST for the same reason, and change nothing.
  if (u.split('?')[0].startsWith('/api/agent-reports/')) return false;
  return true;
}

// The staff-access refusal screen: forget everything and remember nothing until the page is reloaded.
if (typeof window !== 'undefined') {
  window.addEventListener(STAFF_ACCESS_BLOCKED_EVENT, () => {
    blocked = true;
    clearAll();
  });
}

/** Test helper: back to a clean slate, as a fresh page load. */
export function __resetForTests() {
  userId = null;
  blocked = false;
  epoch = 0;
  writes = 0;
  store.clear();
}
